<!-- src/views/LoginView.vue -->
<template>
  <div class="bg-white font-sans selection:bg-[#20FCF1]/30 text-left min-h-screen">
    <!-- 🛡️ MASTER CONTAINER -->
    <div class="max-w-7xl mx-auto px-4 sm:px-0 py-8 md:py-12 animate-fadeIn">
      
      <!-- 1. AUTHORITATIVE COMMAND HEADER -->
      <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden mb-10">
        <!-- Faint Background Layer -->
        <div class="absolute top-0 right-0 p-8 opacity-5">
          <LockClosedIcon class="h-32 w-32 text-white" />
        </div>

        <div class="relative z-10">
          <h2 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none">
            {{ 
              loginState === 'credentials' ? 'System Login' : 
              loginState === 'mfa' ? 'Authorise Access' : 
              loginState === 'enroll_mfa' ? 'Enrol Security' :
              loginState === 'forgot-password' ? 'Reset Protocol' : 'Update Access Key'
            }}
          </h2>
          <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 flex items-center gap-2">
            <span class="w-1.5 h-1.5 bg-[#20FCF1] rounded-full animate-pulse"></span>
            {{ 
              loginState === 'credentials' ? 'Security Gate Active' : 
              loginState === 'mfa' ? 'Multi-Factor Handshake Required' : 
              loginState === 'enroll_mfa' ? 'Enroll Multi-Factor Authentication' :
              'SMS Identity Recovery Active' 
            }}
          </p>
        </div>
      </div>

      <!-- 2. OPERATIONAL BASE SECTION -->
      <div class="bg-white rounded-[40px] border border-slate-100 shadow-2xl p-8 md:p-12 lg:p-16">
        
        <!-- STATE 1: CREDENTIALS (Email & Password Gate) -->
        <form v-if="loginState === 'credentials'" @submit.prevent="handleLogin" class="flex flex-col lg:flex-row gap-8 lg:items-end w-full">
          <div class="flex-1">
            <label for="email" class="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Account Identity</label>
            <input 
              type="email" 
              id="email" 
              v-model="email" 
              required 
              placeholder="operator@domain.co.uk"
              class="form-input-premium"
            />
          </div>
          
          <div class="flex-1">
            <div class="flex justify-between items-center mb-3 ml-1">
              <label for="password" class="block text-[10px] font-black uppercase tracking-widest text-slate-400">Access Key</label>
              <button @click.prevent="loginState = 'forgot-password'" type="button" class="text-[9px] font-black uppercase tracking-widest text-[#20FCF1] hover:text-white hover:bg-[#0A1630] transition-all bg-slate-100 px-3 py-1 rounded-lg border-none cursor-pointer">
                Lost Key?
              </button>
            </div>
            <input 
              type="password" 
              id="password" 
              v-model="password" 
              required 
              placeholder="••••••••••••"
              class="form-input-premium"
            />
          </div>

          <div class="lg:w-1/4">
            <button type="submit" :disabled="isBusy" class="btn-command-primary disabled:opacity-50 border-none cursor-pointer">
              <span v-if="isBusy">Initialising...</span>
              <span v-else>Initialise Protocol</span>
            </button>
          </div>
        </form>

        <!-- STATE 2: MFA CHALLENGE (Universal Handshake) -->
        <form v-else-if="loginState === 'mfa'" @submit.prevent="handleMfaSubmission" class="max-w-2xl mx-auto space-y-8 py-8 animate-fadeIn">
          <div class="text-center bg-slate-50 p-8 rounded-[32px] border border-slate-100">
            <p class="text-sm text-slate-500 font-medium leading-relaxed">
              A secure 6-digit authorisation code has been dispatched to the mobile number registered for <span class="text-[#0A1630] font-bold">{{ email.toLowerCase().trim() }}</span>.
            </p>
          </div>

          <div>
            <label for="mfaCode" class="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 text-center">Identity Token</label>
            <input 
              type="text" 
              id="mfaCode" 
              v-model="mfaCode" 
              required 
              maxlength="6"
              placeholder="000000"
              class="w-full px-5 py-6 bg-slate-50 border border-slate-100 rounded-[32px] text-[#0A1630] text-center text-4xl font-black tracking-[0.5em] shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all border-none"
            />
          </div>

          <button type="submit" :disabled="isBusy" class="btn-command-primary disabled:opacity-50 border-none cursor-pointer">
            <span v-if="isBusy">Validating Token...</span>
            <span v-else>Confirm Identity</span>
          </button>

          <!-- 🛡️ UX CLARITY: FALLBACK ADVICE -->
          <div v-if="showFallbackAdvice" class="bg-[#EBFDF8] border-2 border-[#20FCF1] p-6 rounded-[32px] animate-fadeIn transition-all text-left">
            <div class="flex items-start gap-4">
              <div class="bg-[#20FCF1] p-2 rounded-xl text-[#0A1630]">
                <EnvelopeIcon class="h-6 w-6" />
              </div>
              <div>
                <p class="text-xs font-black uppercase text-[#0A1630] tracking-tight">Signal Delay Detected</p>
                <p class="text-[11px] text-slate-600 mt-1 leading-relaxed">
                  If you haven't received your SMS within 30 seconds, please use the <strong>Email Fallback</strong> button below to receive your token via your inbox.
                </p>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-4 pt-6 border-t border-slate-50">
            <button @click.prevent="triggerEmailFallback" type="button" class="w-full py-4 px-6 border-2 border-slate-200 rounded-[20px] text-[10px] font-black uppercase tracking-widest text-slate-500 hover:border-[#20FCF1] hover:text-[#0A1630] hover:bg-slate-50 transition-all flex items-center justify-center gap-3 cursor-pointer bg-transparent">
              <EnvelopeIcon class="h-4 w-4 text-[#20FCF1]" />
              Request Email Fallback
            </button>
            <button @click.prevent="resetState" type="button" class="text-[10px] font-black uppercase text-slate-300 hover:text-rose-500 transition-colors text-center bg-transparent border-none cursor-pointer">
              Cancel Handshake
            </button>
          </div>
        </form>

        <!-- STATE 3: FORGOT PASSWORD (SMS REQUEST) -->
        <form v-else-if="loginState === 'forgot-password'" @submit.prevent="initiateSmsReset" class="max-w-2xl mx-auto space-y-8 py-8 animate-fadeIn">
          <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100 text-center">
            <div class="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-slate-100 text-[#20FCF1]">
              <KeyIcon class="h-8 w-8" />
            </div>
            <p class="text-sm text-slate-600 font-medium">
              Enter your verified email. Sentinel Safe will transmit a recovery code to your registered mobile device.
            </p>
          </div>

          <div>
            <label for="resetEmail" class="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Recovery Email</label>
            <input 
              type="email" 
              id="resetEmail" 
              v-model="email" 
              required 
              placeholder="operator@domain.co.uk"
              class="form-input-premium"
            />
          </div>

          <button type="submit" :disabled="isBusy" class="btn-command-primary border-none cursor-pointer">
            Request Recovery PIN
          </button>

          <!-- 🛡️ EMAIL PASS RESET FALLBACK (Request 2): Dispatch native password reset email to bypass carrier outtages -->
          <div class="flex flex-col gap-4 pt-6 border-t border-slate-50">
            <button @click.prevent="triggerEmailPasswordReset" type="button" class="w-full py-4 px-6 border-2 border-slate-200 rounded-[20px] text-[10px] font-black uppercase tracking-widest text-slate-500 hover:border-[#20FCF1] hover:text-[#0A1630] hover:bg-slate-50 transition-all flex items-center justify-center gap-3 cursor-pointer bg-transparent">
              <EnvelopeIcon class="h-4 w-4 text-[#20FCF1]" />
              Send Secure Reset Link to Email Inbox
            </button>
          </div>

          <div class="flex justify-center pt-4">
            <button @click.prevent="resetState" type="button" class="text-[10px] font-black uppercase text-slate-400 hover:text-[#0A1630] transition-colors flex items-center gap-2 bg-transparent border-none cursor-pointer">
               <ArrowLeftIcon class="h-3 w-3" /> Return to Login
            </button>
          </div>
        </form>

        <!-- STATE 4: RESET COMPLETION (PIN + NEW PASS) -->
        <form v-else-if="loginState === 'reset-verification'" @submit.prevent="handleSmsResetCompletion" class="max-w-2xl mx-auto space-y-6 py-8 animate-fadeIn">
          <div class="bg-indigo-50 p-8 rounded-[32px] border border-indigo-100 text-indigo-900 font-bold uppercase tracking-tight text-center mb-4">
            Identity Verification PIN sent via SMS
          </div>

          <div>
            <label class="block text-[10px] font-black uppercase text-slate-400 mb-2 ml-1">Recovery PIN (From SMS)</label>
            <input type="text" v-model="resetSmsCode" required maxlength="6" class="form-input-premium text-center text-2xl tracking-[0.5em]" placeholder="000000" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-[10px] font-black uppercase text-slate-400 mb-2 ml-1">New Access Key</label>
              <input type="password" v-model="newPassword" required class="form-input-premium" placeholder="••••••••" />
            </div>
            <div>
              <label class="block text-[10px] font-black uppercase text-slate-400 mb-2 ml-1">Confirm New Key</label>
              <input type="password" v-model="confirmPassword" required class="form-input-premium" placeholder="••••••••" />
            </div>
          </div>

          <button type="submit" :disabled="isBusy" class="btn-command-primary bg-[#20FCF1] !text-[#0A1630] mt-4 border-none cursor-pointer">
            Update Access Key & Log In
          </button>

          <!-- 🛡️ EMAIL PASS RESET FALLBACK (Request 2): Backup route on PIN page -->
          <div class="flex flex-col gap-4 pt-6 border-t border-slate-50">
            <button @click.prevent="triggerEmailPasswordReset" type="button" class="w-full py-4 px-6 border-2 border-slate-200 rounded-[20px] text-[10px] font-black uppercase tracking-widest text-slate-500 hover:border-[#20FCF1] hover:text-[#0A1630] hover:bg-slate-50 transition-all flex items-center justify-center gap-3 cursor-pointer bg-transparent">
              <EnvelopeIcon class="h-4 w-4 text-[#20FCF1]" />
              Send Secure Reset Link to Email Inbox
            </button>
          </div>

          <button @click.prevent="loginState = 'forgot-password'" type="button" class="w-full text-center text-[10px] font-black uppercase text-slate-400 hover:text-indigo-600 transition-colors bg-transparent border-none cursor-pointer">
            Resend Recovery PIN
          </button>
        </form>

        <!-- STATE 5: MFA ENROLMENT (For New Users) -->
        <form v-else-if="loginState === 'enroll_mfa'" @submit.prevent="enrollStep === 'input' ? sendEnrollmentCode() : submitEnrollmentCode()" class="max-w-2xl mx-auto space-y-8 py-8 animate-fadeIn">
          <div class="text-center bg-slate-50 p-8 rounded-[32px] border border-slate-100">
            <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight mb-2">Set Up Multi-Factor Security</h3>
            <p class="text-xs text-slate-500 font-medium leading-relaxed">
              To secure your Sentinel Safe account, multi-factor authentication is required. Please verify and register your active mobile number.
            </p>
          </div>

          <div v-if="enrollStep === 'input'" class="space-y-6">
            <div>
              <label for="enrollPhone" class="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Mobile Phone Number</label>
              <input 
                type="tel" 
                id="enrollPhone" 
                v-model="enrollPhoneNumber" 
                required 
                placeholder="+447000000000"
                class="form-input-premium text-center text-xl font-bold"
              />
              <p class="text-[9px] text-slate-400 font-semibold mt-2 text-center">Use international format (e.g., +447846896955)</p>
            </div>

            <button type="submit" :disabled="isBusy" class="btn-command-primary border-none cursor-pointer">
              <span v-if="isBusy">Sending Code...</span>
              <span v-else>Send Enrolment SMS</span>
            </button>
          </div>

          <div v-else-if="enrollStep === 'verify'" class="space-y-6">
            <div class="bg-indigo-50 p-6 rounded-2xl border border-indigo-100 text-center">
              <p class="text-xs text-indigo-900 font-bold">Verification code sent to {{ enrollPhoneNumber }}</p>
            </div>

            <div>
              <label for="enrollCodeInput" class="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 text-center">Enrolment Code</label>
              <input 
                type="text" 
                id="enrollCodeInput" 
                v-model="enrollCode" 
                required 
                maxlength="6"
                placeholder="000000"
                class="w-full px-5 py-6 bg-slate-50 border border-slate-100 rounded-[32px] text-[#0A1630] text-center text-4xl font-black tracking-[0.5em] shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none border-none"
              />
            </div>

            <button type="submit" :disabled="isBusy" class="btn-command-primary bg-[#20FCF1] !text-[#0A1630] border-none cursor-pointer">
              <span v-if="isBusy">Verifying...</span>
              <span v-else>Verify & Enrol Device</span>
            </button>

            <button @click.prevent="enrollStep = 'input'" type="button" class="w-full text-center text-[10px] font-black uppercase text-slate-400 hover:text-[#0A1630] bg-transparent border-none cursor-pointer">
              Change Phone Number
            </button>
          </div>
        </form>

        <!-- DYNAMIC ALERTS -->
        <div v-if="errorMessage" class="mt-12 p-6 rounded-[24px] bg-rose-50 border border-rose-100 text-rose-600 text-xs font-black text-center uppercase tracking-widest animate-pulse flex items-center justify-center gap-3">
          <ExclamationTriangleIcon class="h-5 w-5 animate-bounce" />
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="mt-12 p-6 rounded-[24px] bg-[#EBFDF8] border border-[#20FCF1]/20 text-[#22C55E] text-xs font-black text-center uppercase tracking-widest flex items-center justify-center gap-3">
          <ShieldCheckIcon class="h-5 w-5" />
          {{ successMessage }}
        </div>

        <!-- RECAPTCHA ANCHOR -->
        <div id="recaptcha-container"></div>

        <div class="text-center text-[10px] font-bold tracking-widest text-slate-300 mt-16 uppercase leading-relaxed">
          <p class="flex items-center justify-center gap-3 text-slate-400">
            <ShieldCheckIcon class="h-4 w-4 text-[#20FCF1]" />
            Identity Protocol Synchronised • Multi-Factor Active
          </p>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { db, auth } from '@/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { 
  signInWithEmailAndPassword, 
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  sendPasswordResetEmail
} from 'firebase/auth';
import { useUserStore } from '@/stores/userStore';
import { 
  LockClosedIcon, 
  ShieldCheckIcon, 
  ExclamationTriangleIcon, 
  ArrowLeftIcon, 
  KeyIcon,
  EnvelopeIcon 
} from '@heroicons/vue/24/outline';

const router = useRouter();
const userStore = useUserStore();

const loginState = ref('credentials'); 
const email = ref('');
const password = ref('');
const mfaCode = ref('');

// UX Helpers
const showFallbackAdvice = ref(false);
let adviceTimer = null;

// Password Reset States
const resetSmsCode = ref('');
const newPassword = ref('');
const confirmPassword = ref('');

const errorMessage = ref('');
const successMessage = ref('');
const isBusy = ref(false);

// State for Native MFA (Admin flow)
const mfaResolver = ref(null);
const verificationId = ref('');

// State for Custom MFA (Legacy / Fallback flow)
const isCustomMfaFlow = ref(false);
const customSessionToken = ref(Math.random().toString(36).substring(2, 15));

// State for Native MFA Enrolment Flow
const enrollStep = ref('input');
const enrollPhoneNumber = ref('');
const enrollCode = ref('');
const enrollVerificationId = ref('');

onUnmounted(() => {
  if (window.recaptchaVerifier) {
    window.recaptchaVerifier.clear();
    window.recaptchaVerifier = null;
  }
  if (adviceTimer) clearTimeout(adviceTimer);
});

const resetState = () => {
  loginState.value = 'credentials';
  mfaCode.value = '';
  errorMessage.value = '';
  successMessage.value = '';
  isBusy.value = false;
  isCustomMfaFlow.value = false;
  showFallbackAdvice.value = false;
  if (adviceTimer) clearTimeout(adviceTimer);
  resetSmsCode.value = '';
  newPassword.value = '';
  confirmPassword.value = '';
  enrollStep.value = 'input';
  enrollPhoneNumber.value = '';
  enrollCode.value = '';
  enrollVerificationId.value = '';
};

const setupRecaptcha = async () => {
  const container = document.getElementById('recaptcha-container');
  if (container) container.innerHTML = ''; 
  try {
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
    await window.recaptchaVerifier.render();
  } catch (error) {
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'normal' });
  }
};

/**
 * 🛡️ HANDLE LOGIN
 * Re-aligned to fetch Firestore data and process emergency MFA exemptions on credential handshake.
 */
const handleLogin = async () => {
  errorMessage.value = '';
  isBusy.value = true;
  const normalizedEmail = email.value.toLowerCase().trim();
  
  try {
    const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password.value);
    
    // 🛡️ Fetch the user's Firestore profile to check for Emergency Reset or MFA exemptions
    const pSnap = await getDoc(doc(db, 'userProfiles', userCredential.user.uid));
    let profile = {};
    if (pSnap.exists()) {
      profile = pSnap.data();
      // If user is marked as exempt, has MFA disabled, or is a Super Admin
      if (profile.role === 'super-admin' || profile.mfaExempt === true || profile.mfaEnabled === false) {
        await processSuccessfulLogin(userCredential.user);
        return;
      }
      
      // 🛡️ OPTION 2: FIRST LOGIN GRACE
      // If they are still pending onboarding, bypass MFA enrolment and let them enter
      if (profile.status === 'pending_onboarding') {
        await processSuccessfulLogin(userCredential.user);
        return;
      }
    }

    // Check if user has Native Firebase MFA factors enrolled (Enabled users)
    if (!userCredential.user.multiFactor?.enrolledFactors?.length) {
      // 🛡️ INLINE NATIVE MFA ENROLMENT (For Onboarded, Active users lacking setup)
      loginState.value = 'enroll_mfa';
      enrollStep.value = 'input';
      
      enrollPhoneNumber.value = profile.mobilePhoneNumber || profile.phoneNumber || '';
      await setupRecaptcha();
    } else {
      // 🛡️ NATIVE MFA PATH
      await processSuccessfulLogin(userCredential.user);
    }
  } catch (error) {
    if (error.code === 'auth/multi-factor-auth-required') {
      mfaResolver.value = getMultiFactorResolver(auth, error);
      loginState.value = 'mfa';
      isCustomMfaFlow.value = false;
      await setupRecaptcha();
      try {
        const phoneInfoOptions = { multiFactorHint: mfaResolver.value.hints[0], session: mfaResolver.value.session };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        verificationId.value = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier);
      } catch (mfaError) {
        errorMessage.value = 'Identity dispatch failure. Please refresh.';
      }
    } else {
      handleAuthError(error);
    }
  } finally {
    isBusy.value = false;
  }
};

/**
 * 🛡️ SEND ENROLMENT CODE
 * Dispatches verification token to register the device natively with E.164 compliance checking.
 */
const sendEnrollmentCode = async () => {
  if (!enrollPhoneNumber.value) {
    errorMessage.value = 'Phone number is required for verification.';
    return;
  }
  errorMessage.value = '';
  isBusy.value = true;
  await setupRecaptcha();

  // 🛡️ UK AUTO-FORMATTER PROTOCOL (E.164 compliance): 
  // Convert standard local UK numbers (07xxx...) to international format (+447xxx...)
  let formattedPhone = enrollPhoneNumber.value.trim().replace(/\s+/g, '');
  if (formattedPhone.startsWith('0')) {
    formattedPhone = '+44' + formattedPhone.substring(1);
  }
  enrollPhoneNumber.value = formattedPhone;

  try {
    const session = await multiFactor(auth.currentUser).getSession();
    const phoneInfoOptions = {
      phoneNumber: formattedPhone,
      session: session
    };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    enrollVerificationId.value = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      window.recaptchaVerifier
    );
    enrollStep.value = 'verify';
    successMessage.value = 'Verification PIN sent successfully.';
    setTimeout(() => { successMessage.value = ''; }, 4000);
  } catch (error) {
    console.error("MFA Enrolment Error:", error);
    errorMessage.value = 'MFA dispatch failure: ' + error.message;
  } finally {
    isBusy.value = false;
  }
};

/**
 * 🛡️ SUBMIT ENROLMENT CODE
 * Commits the native multi-factor assertion to the user's Auth profile.
 */
const submitEnrollmentCode = async () => {
  if (!enrollCode.value) {
    errorMessage.value = 'Verification PIN is required.';
    return;
  }
  errorMessage.value = '';
  isBusy.value = true;
  try {
    const cred = PhoneAuthProvider.credential(enrollVerificationId.value, enrollCode.value);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactor(auth.currentUser).enroll(multiFactorAssertion, "Primary SMS Device");
    
    // Successfully enrolled. Record active status in user Firestore profile
    await setDoc(doc(db, 'userProfiles', auth.currentUser.uid), {
      mfaEnabled: true
    }, { merge: true });

    successMessage.value = 'MFA successfully enrolled! Access authorized.';
    setTimeout(async () => {
      await processSuccessfulLogin(auth.currentUser);
    }, 2000);
  } catch (error) {
    console.error("MFA Code Verification Error:", error);
    errorMessage.value = 'Invalid code. Enrolment verification failed.';
  } finally {
    isBusy.value = false;
  }
};

/**
 * 🛡️ HANDLE MFA SUBMISSION
 */
const handleMfaSubmission = async () => {
  if (isCustomMfaFlow.value) {
    await completeCustomHandshake();
  } else {
    await verifyNativeMfaCode();
  }
};

const completeCustomHandshake = async () => {
  errorMessage.value = '';
  isBusy.value = true;
  const normalizedEmail = email.value.toLowerCase().trim();
  try {
    const user = await userStore.verifySmsMfaHandshake(normalizedEmail, customSessionToken.value, mfaCode.value);
    await processSuccessfulLogin(user);
  } catch (error) {
    errorMessage.value = error.message || 'Authorisation code invalid.';
  } finally {
    isBusy.value = false;
  }
};

const verifyNativeMfaCode = async () => {
  errorMessage.value = '';
  isBusy.value = true;
  try {
    const cred = PhoneAuthProvider.credential(verificationId.value, mfaCode.value);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    const userCredential = await mfaResolver.value.resolveSignIn(multiFactorAssertion);
    await processSuccessfulLogin(userCredential.user);
  } catch (error) {
    errorMessage.value = 'Unauthorised code. Verification failed.';
  } finally {
    isBusy.value = false;
  }
};

/** 🛡️ INITIATE SMS RESET */
const initiateSmsReset = async () => {
  if (!email.value) { errorMessage.value = 'Recovery email required.'; return; }
  isBusy.value = true; errorMessage.value = '';
  const normalizedEmail = email.value.toLowerCase().trim();
  try {
    await userStore.dispatchPasswordResetHandshake(normalizedEmail);
    loginState.value = 'reset-verification';
    successMessage.value = 'Recovery PIN dispatched via SMS.';
    setTimeout(() => { successMessage.value = ''; }, 5000);
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    isBusy.value = false;
  }
};

/** 🛡️ NATIVE EMAIL PASSWORD RESET FALLBACK */
const triggerEmailPasswordReset = async () => {
  if (!email.value) { errorMessage.value = 'Recovery email required.'; return; }
  isBusy.value = true; errorMessage.value = '';
  const normalizedEmail = email.value.toLowerCase().trim();
  try {
    await sendPasswordResetEmail(auth, normalizedEmail);
    successMessage.value = 'Secure reset link has been dispatched to your email inbox.';
    setTimeout(() => { successMessage.value = ''; }, 6000);
  } catch (error) {
    errorMessage.value = 'Email dispatch failure: ' + error.message;
  } finally {
    isBusy.value = false;
  }
};

/** 🛡️ COMPLETE SMS RESET */
const handleSmsResetCompletion = async () => {
  if (newPassword.value !== confirmPassword.value) { errorMessage.value = 'Keys do not match.'; return; }
  if (newPassword.value.length < 8) { errorMessage.value = 'Key must be at least 8 characters.'; return; }
  
  isBusy.value = true; errorMessage.value = '';
  const normalizedEmail = email.value.toLowerCase().trim();
  try {
    await userStore.completePasswordResetHandshake(normalizedEmail, resetSmsCode.value, newPassword.value);
    successMessage.value = 'Access Key Updated. Initialise standard login.';
    loginState.value = 'credentials';
    password.value = ''; 
    newPassword.value = ''; 
    confirmPassword.value = ''; 
    resetSmsCode.value = '';
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    isBusy.value = false;
  }
};

const triggerEmailFallback = async () => {
  errorMessage.value = '';
  isBusy.value = true;
  const normalizedEmail = email.value.toLowerCase().trim();
  const session = isCustomMfaFlow.value ? customSessionToken.value : mfaResolver.value?.session;
  try {
    await userStore.triggerEmailMfaFallback(normalizedEmail, session);
    successMessage.value = 'Fallback initiated. Check your inbox.';
    // Clear advice once they've taken action
    showFallbackAdvice.value = false;
  } catch (error) {
    errorMessage.value = 'Handshake failed.';
  } finally {
    isBusy.value = false;
  }
};

const processSuccessfulLogin = async (user) => {
  await userStore.fetchUser(user);
  if (userStore.userProfile?.status === 'pending_onboarding') {
    router.push('/onboarding-video'); 
  } else {
    router.push('/dashboard');
  }
};

const handleAuthError = (error) => {
  switch (error.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      errorMessage.value = 'Invalid credentials. Access denied.';
      break;
    case 'auth/user-not-found':
      errorMessage.value = 'Unrecognised account identity.';
      break;
    default:
      errorMessage.value = error.message;
      break;
  }
};
</script>

<style scoped>
.form-input-premium {
  @apply w-full px-6 py-5 bg-slate-50 border border-slate-200 rounded-[20px] text-[#0A1630] font-bold shadow-inner focus:ring-2 focus:ring-[#20FCF1] focus:bg-white focus:border-transparent outline-none transition-all duration-300 sm:text-sm;
}

.btn-command-primary {
  @apply w-full py-5 px-4 bg-[#0A1630] text-white rounded-[20px] text-center font-black uppercase tracking-widest text-[11px] shadow-xl hover:bg-slate-800 transform hover:-translate-y-0.5 active:scale-95 transition-all duration-300 flex items-center justify-center;
}

.animate-fadeIn { animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
</style>